import { ErrorHandler, type Provider } from '@angular/core'
import { createErrorHandler, init as angularInit } from '@sentry/angular'
import { browserTracingIntegration, init } from '@sentry/capacitor'
import type { CapacitorOptions } from '@sentry/capacitor'
import { captureException } from './error-capture.helper'

type SentryEnvironmentConfig = {
  env: string
  release: string
  dist: string
}

const SENTRY_DSN = 'https://d9829813f6ca46bba3ff2e805c8c6db3@o492374.ingest.sentry.io/5559533'

type SentryException = NonNullable<
  NonNullable<Parameters<NonNullable<CapacitorOptions['beforeSend']>>[0]['exception']>['values']
>[number]

// Injected by the iOS Google Search App when detecting images/links for its
// native context menu. The script recurses up the DOM and can overflow the
// stack on pages with unusual shadow-DOM or stacking-context structures.
const GSA_CONTEXT_MENU_FUNCTIONS = new Set(['findTopmostVisibleElement', 'isOpaqueElement', 'isImage', 'checkForImage'])

/**
 * The iOS Google Search App (GSA) injects a context-menu script that walks
 * the DOM to find the topmost image/link at a tap point. On some pages it
 * recurses until the call stack overflows, producing a RangeError whose
 * frames are only the injected helpers (`findTopmostVisibleElement`,
 * `isOpaqueElement`, `isImage`, `checkForImage`). This is third-party browser
 * noise, not an app bug; the current Ionic build no longer contains this
 * helper and it is absent from our deployed chunks.
 */
const isGsaContextMenuStackOverflow = (exception: SentryException): boolean => {
  if (exception.type !== 'RangeError') {
    return false
  }
  if (!/Maximum call stack size exceeded/i.test(exception.value ?? '')) {
    return false
  }
  const frames = exception.stacktrace?.frames
  if (!frames || frames.length === 0) {
    return false
  }
  // Every frame is one of the injected GSA helpers; no app frames are involved.
  return frames.every((frame) => frame.function && GSA_CONTEXT_MENU_FUNCTIONS.has(frame.function))
}

/**
 * Drops the Ionic ion-menu teardown TypeError that occurs when
 * gesture-controller touches document.body after the document has
 * already been torn down. This is an unfixable upstream bug in Ionic
 * (verified: no null guard in gesture-controller.ts on main, no fix
 * in v8.8.9→v8.8.10). Frame matching is impossible client-side because
 * beforeSend runs before source-map symbolication.
 */
export const beforeSendFilter: NonNullable<CapacitorOptions['beforeSend']> = (event) => {
  const firstException = event.exception?.values?.[0]
  if (!firstException) {
    return event
  }

  const isTypeError = firstException.type === 'TypeError'
  const isClassListNullMessage =
    /Cannot read properties of null \(reading 'classList'\)/.test(firstException.value ?? '') ||
    /null is not an object \(evaluating .*classList/.test(firstException.value ?? '')
  const isUnhandledRejection = firstException.mechanism?.type?.endsWith('onunhandledrejection') ?? false
  const isUnhandled = firstException.mechanism?.handled !== true

  if (isTypeError && isClassListNullMessage && isUnhandledRejection && isUnhandled) {
    return null
  }

  if (isGsaContextMenuStackOverflow(firstException)) {
    return null
  }

  return event
}

export const initializeSentry = ({ env, release, dist }: SentryEnvironmentConfig): void => {
  if (env === 'development') {
    return
  }

  init(
    {
      dsn: SENTRY_DSN,
      environment: env,
      release,
      dist,
      enableAutoSessionTracking: true,
      integrations: [browserTracingIntegration()],
      tracePropagationTargets: ['localhost', 'pregnantwithdiabetes.vercel.app'],
      denyUrls: [/utm_source=monitoring/i],
      tracesSampleRate: 1.0,
      ignoreErrors: [
        /Loading chunk 7005 failed/i,
        /TrackerStorageType is not defined/i,
        /Invalid call to runtime\.sendMessage\(\)/i,
        // Blink internal IndexedDB error family. Sentry core already ignores the
        // "simulateEvent" variant; we also drop "update", "put", etc. variants.
        // These surface through localforage/@ionic/storage on certain browsers
        // when IDB is transiently unavailable, not through an app bug.
        /Object Not Found Matching Id:\d+, MethodName:\w+, ParamCount:\d+/,
      ],
      beforeSend: beforeSendFilter,
    },
    angularInit,
  )
}

export const provideSentryErrorHandler = (): Provider => ({
  provide: ErrorHandler,
  useValue: createErrorHandler({ showDialog: false }),
})

export const handleBootstrapError = (error: unknown): void => {
  captureException(error, 'error', 'bootstrap-application')
  // eslint-disable-next-line no-console
  console.error(error)
}
